import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { SharedTabletLockOverlay } from '../../components/SharedTabletLockOverlay.js';
import { useAccountAccessState } from '../data/queries.js';
import { getDeviceId } from '../db/local.js';
import { confirmDialog } from '../ui/dialogs.js';
import { useAuth } from './AuthProvider.js';
import { shouldShowSharedTabletGate } from './accountAccess.js';
import {
  setActiveSharedTabletOperator,
  setSharedTabletModeActive,
} from './activeOperatorSession.js';
import {
  cacheOnlineOperatorGrant,
  cacheVerifiedOperatorPinPolicy,
  readCachedOperatorPinRequired,
  readCachedOperatorRoster,
  reconcileCachedOperatorRoster,
  unlockCachedOperator,
  type ActiveSharedTabletOperator,
  type OperatorCacheScope,
} from './operatorCredentialStore.js';
import {
  operatorExternalActivityActive,
  subscribeOperatorExternalActivity,
} from './operatorExternalActivity.js';
import {
  activateSharedTabletOperatorGrant,
  deactivateSharedTabletOperator,
  fetchSharedTabletPinRequired,
  fetchSharedTabletOperators,
  unlockSharedTabletOperator,
  unlockSharedTabletOperatorWithoutPin,
} from './sharedTabletOperatorApi.js';
import type { SharedTabletOperator } from './sharedTabletOperatorTypes.js';
import {
  SHARED_TABLET_IDLE_TIMEOUT_MS,
  shouldLockSharedTabletOnBackground,
} from './operatorLockPolicy.js';

interface SharedTabletOperatorContextValue {
  isSharedTablet: boolean;
  locked: boolean;
  activeOperator: ActiveSharedTabletOperator | null;
  lock: () => void;
  changeOperator: () => void;
  exitAccount: () => Promise<void>;
}

const SharedTabletOperatorContext = createContext<SharedTabletOperatorContextValue | null>(null);

function isNetworkFailure(error: unknown): boolean {
  if (!navigator.onLine) return true;
  const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  return message.includes('fetch') || message.includes('network') || message.includes('failed to connect');
}

export function SharedTabletOperatorProvider({ children }: { children: ReactNode }): JSX.Element {
  const { session, profile, signOut } = useAuth();
  const accountAccess = useAccountAccessState();
  const [operators, setOperators] = useState<SharedTabletOperator[]>([]);
  const [pinRequired, setPinRequired] = useState(true);
  const [activeOperator, setActiveOperator] = useState<ActiveSharedTabletOperator | null>(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<OperatorCacheScope | null>(null);
  const [offline, setOffline] = useState(!navigator.onLine);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scopeRef = useRef<OperatorCacheScope | null>(null);
  const rosterRequest = useRef(0);

  const isSharedTablet = !!session && accountAccess.resolved && accountAccess.isSharedTablet;
  const locked = isSharedTablet && activeOperator === null;
  const overlayVisible = shouldShowSharedTabletGate({
    hasSession: !!session,
    isSharedTablet: accountAccess.isSharedTablet,
    resolved: accountAccess.resolved,
    hasActiveOperator: activeOperator !== null,
  });

  useEffect(() => {
    setSharedTabletModeActive(isSharedTablet);
    return () => setSharedTabletModeActive(false);
  }, [isSharedTablet]);

  const clearOperator = useCallback(() => {
    const currentScope = scopeRef.current;
    if (currentScope && navigator.onLine) {
      void deactivateSharedTabletOperator(currentScope.deviceId).catch(() => undefined);
    }
    setActiveOperator(null);
    setActiveSharedTabletOperator(null);
    setSelectedOperatorId(null);
    setPin('');
    setError(null);
  }, []);

  const refreshRoster = useCallback(async (nextScope: OperatorCacheScope) => {
    const request = ++rosterRequest.current;
    const isCurrent = () => scopeRef.current === nextScope && rosterRequest.current === request;
    setLoadingRoster(true);
    setError(null);
    try {
      const [cached, cachedPinRequired] = await Promise.all([
        readCachedOperatorRoster(nextScope), readCachedOperatorPinRequired(nextScope),
      ]);
      if (!isCurrent()) return;
      setPinRequired(cachedPinRequired);
      const cachedAvailable = cachedPinRequired ? cached.filter((operator) => operator.pin_enabled) : cached;
      setOperators(cachedAvailable);
      if (!navigator.onLine) {
        if (cachedAvailable.length === 0) setError('Elenco operatori non disponibile offline. Collegati a Internet e riprova.');
        return;
      }
      const required = await fetchSharedTabletPinRequired();
      if (!isCurrent()) return;
      // Persist a stricter policy even if the subsequent roster request fails.
      await cacheVerifiedOperatorPinPolicy(nextScope, required);
      if (!isCurrent()) return;
      setPinRequired(required);
      setOperators(required ? cached.filter((operator) => operator.pin_enabled) : cached);
      const fresh = await fetchSharedTabletOperators();
      if (!isCurrent()) return;
      const available = required ? fresh.filter((operator) => operator.pin_enabled) : fresh;
      await reconcileCachedOperatorRoster(nextScope, fresh, required);
      if (!isCurrent()) return;
      setOperators(available);
    } catch (refreshError) {
      if (isCurrent()) {
        setError(isNetworkFailure(refreshError)
          ? 'Impossibile aggiornare l’elenco operatori. Controlla la connessione e riprova.'
          : 'Impossibile caricare l’elenco operatori. Riprova.');
      }
    } finally {
      if (isCurrent()) setLoadingRoster(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    clearOperator();
    setOperators([]);
    setPinRequired(true);
    setScope(null);
    if (!isSharedTablet || !profile?.active_tenant_id || !profile.id) return undefined;

    void getDeviceId().then((deviceId) => {
      if (cancelled) return;
      const nextScope = {
        tenantId: profile.active_tenant_id!,
        tabletUserId: profile.id,
        deviceId,
      };
      scopeRef.current = nextScope;
      setScope(nextScope);
    });
    return () => {
      cancelled = true;
      const currentScope = scopeRef.current;
      if (currentScope && navigator.onLine) {
        void deactivateSharedTabletOperator(currentScope.deviceId).catch(() => undefined);
      }
      scopeRef.current = null;
    };
  }, [clearOperator, isSharedTablet, profile?.active_tenant_id, profile?.id]);

  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (!isSharedTablet || !activeOperator) return;
    idleTimer.current = setTimeout(() => {
      if (!operatorExternalActivityActive()) clearOperator();
    }, SHARED_TABLET_IDLE_TIMEOUT_MS);
  }, [activeOperator, clearOperator, isSharedTablet]);

  useEffect(() => {
    const refreshNetworkState = () => setOffline(!navigator.onLine);
    window.addEventListener('online', refreshNetworkState);
    window.addEventListener('offline', refreshNetworkState);
    return () => {
      window.removeEventListener('online', refreshNetworkState);
      window.removeEventListener('offline', refreshNetworkState);
    };
  }, []);

  useEffect(() => {
    if (!isSharedTablet || !activeOperator || offline) return;
    void activateSharedTabletOperatorGrant({
      grantId: activeOperator.grantId,
      grantToken: activeOperator.grantToken,
      tenantPersonnelId: activeOperator.tenantPersonnelId,
      deviceId: activeOperator.deviceId,
    }).catch((activationError: unknown) => {
      if (!isNetworkFailure(activationError)) clearOperator();
    });
  }, [activeOperator, clearOperator, isSharedTablet, offline]);

  useEffect(() => {
    if (scope && locked) void refreshRoster(scope);
  }, [locked, offline, refreshRoster, scope]);

  useEffect(() => {
    if (!isSharedTablet || !activeOperator) return undefined;
    resetIdleTimer();
    const onActivity = () => resetIdleTimer();
    const activityEvents: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'touchstart',
      'wheel',
    ];
    for (const eventName of activityEvents) {
      window.addEventListener(eventName, onActivity, { passive: true, capture: true });
    }

    const lockForBackground = () => {
      if (shouldLockSharedTabletOnBackground({
        isSharedTablet,
        hasActiveOperator: activeOperator !== null,
        externalActivityActive: operatorExternalActivityActive(),
      })) clearOperator();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') lockForBackground();
      else resetIdleTimer();
    };
    document.addEventListener('visibilitychange', onVisibility);
    const unsubscribeExternal = subscribeOperatorExternalActivity(resetIdleTimer);
    let removeNativeListener: (() => void) | null = null;
    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) lockForBackground();
      else resetIdleTimer();
    }).then((handle) => {
      removeNativeListener = () => { void handle.remove(); };
    }).catch(() => undefined);

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      for (const eventName of activityEvents) {
        window.removeEventListener(eventName, onActivity, { capture: true });
      }
      document.removeEventListener('visibilitychange', onVisibility);
      unsubscribeExternal();
      removeNativeListener?.();
    };
  }, [activeOperator, clearOperator, isSharedTablet, resetIdleTimer]);

  useEffect(() => {
    const appContent = document.querySelector('ion-tabs');
    if (!appContent) return undefined;
    if (overlayVisible) {
      appContent.setAttribute('inert', '');
      appContent.setAttribute('aria-hidden', 'true');
    } else {
      appContent.removeAttribute('inert');
      appContent.removeAttribute('aria-hidden');
    }
    return () => {
      appContent.removeAttribute('inert');
      appContent.removeAttribute('aria-hidden');
    };
  }, [overlayVisible]);

  const unlock = useCallback(async () => {
    if (!scope || !selectedOperatorId || (pinRequired && pin.length !== 4) || busy || loadingRoster) return;
    setBusy(true);
    setError(null);
    try {
      if (navigator.onLine) {
        try {
          const result = pinRequired
            ? await unlockSharedTabletOperator({ tenantPersonnelId: selectedOperatorId, pin, deviceId: scope.deviceId })
            : await unlockSharedTabletOperatorWithoutPin({ tenantPersonnelId: selectedOperatorId, deviceId: scope.deviceId });
          if (scopeRef.current !== scope) return;
          if (result.status === 'invalid_pin') {
            setError('PIN non corretto. Ricontrolla le 4 cifre.');
            return;
          }
          if (result.status === 'locked') {
            setError(`Troppi tentativi. Riprova tra ${Math.max(1, Math.ceil(result.retry_after_seconds / 60))} min.`);
            return;
          }
          const operator = await cacheOnlineOperatorGrant({
            scope,
            pin: pinRequired ? pin : `shared-tablet-no-pin:${scope.deviceId}`,
            pinRequired,
            grant: result,
          });
          if (scopeRef.current !== scope) return;
          setActiveOperator(operator);
          setActiveSharedTabletOperator(operator);
          setPin('');
          resetIdleTimer();
          return;
        } catch (onlineError) {
          if (!isNetworkFailure(onlineError)) throw onlineError;
        }
      }

      const cached = await unlockCachedOperator({
        scope,
        tenantPersonnelId: selectedOperatorId,
        pin: pinRequired ? pin : `shared-tablet-no-pin:${scope.deviceId}`,
      });
      if (scopeRef.current !== scope) return;
      if (cached.status === 'ok' && cached.activeOperator) {
        setActiveOperator(cached.activeOperator);
        setActiveSharedTabletOperator(cached.activeOperator);
        setPin('');
        resetIdleTimer();
      } else if (cached.status === 'invalid_pin') {
        setError('PIN non corretto. Ricontrolla le 4 cifre.');
      } else if (cached.status === 'locked') {
        setError(`Troppi tentativi. Riprova tra ${Math.max(1, Math.ceil(cached.retryAfterSeconds / 60))} min.`);
      } else if (cached.status === 'expired') {
        setError('Accesso offline scaduto. Collegati a Internet per rinnovarlo.');
      } else {
        setError('Questa persona non ha ancora effettuato un accesso online su questo tablet.');
      }
    } catch {
      setError('Accesso non verificabile. Controlla la connessione e riprova.');
    } finally {
      setBusy(false);
    }
  }, [busy, loadingRoster, pin, pinRequired, resetIdleTimer, scope, selectedOperatorId]);

  const signOutTablet = useCallback(async () => {
    if (busy) return;
    const confirmed = await confirmDialog(
      isSharedTablet
        ? 'Il tablet tornerà alla schermata email e password. Per usare di nuovo le funzioni operative sarà necessario accedere a un account autorizzato.'
        : 'Il dispositivo tornerà alla schermata di accesso.',
      { header: 'Esci dall’account', okText: 'Esci', danger: true },
    );
    if (!confirmed) return;

    setBusy(true);
    clearOperator();
    try {
      await signOut();
    } catch {
      setError('Non è stato possibile uscire dall’account. Riprova tra poco.');
    } finally {
      setBusy(false);
    }
  }, [busy, clearOperator, isSharedTablet, signOut]);

  const context = useMemo<SharedTabletOperatorContextValue>(() => ({
    isSharedTablet,
    locked,
    activeOperator,
    lock: clearOperator,
    changeOperator: clearOperator,
    exitAccount: signOutTablet,
  }), [activeOperator, clearOperator, isSharedTablet, locked, signOutTablet]);

  return (
    <SharedTabletOperatorContext.Provider value={context}>
      {children}
      {overlayVisible && createPortal(
        <SharedTabletLockOverlay
          operators={operators}
          selectedOperatorId={selectedOperatorId}
          pin={pin}
          pinRequired={pinRequired}
          busy={busy}
          loading={!accountAccess.resolved || loadingRoster || scope === null}
          offline={offline}
          error={error}
          onSelect={(operatorId) => {
            setSelectedOperatorId(operatorId);
            setPin('');
            setError(null);
          }}
          onPinChange={(nextPin) => {
            setPin(nextPin);
            setError(null);
          }}
          onUnlock={() => { void unlock(); }}
          onRefresh={() => { if (scope) void refreshRoster(scope); }}
          onSignOut={() => { void signOutTablet(); }}
        />,
        document.body,
      )}
    </SharedTabletOperatorContext.Provider>
  );
}

export function useSharedTabletOperator(): SharedTabletOperatorContextValue {
  const context = useContext(SharedTabletOperatorContext);
  if (!context) throw new Error('useSharedTabletOperator must be used inside SharedTabletOperatorProvider');
  return context;
}
